import React from 'react';
import {
    Edit,
    Trash2,
    Plus,
    ExternalLink
} from 'lucide-react';

import { DEVICE_TYPE_MAP } from './houseHelpers';

export const InfoLine = ({ label, value, highlight = false }) => {
    return (
        <div className="flex items-center justify-between">
            <span className="text-slate-400">
                {label}:
            </span>

            <span className={highlight ? 'text-red-400 font-medium' : 'text-white'}>
                {value || '—'}
            </span>
        </div>
    );
};

export const DeviceItem = ({ device, canChangeHouses, onEdit, onDelete }) => {
    const isVerificationExpired = () => {
        if (!device.verificationValidUntil) return false;

        const validUntil = new Date(
            device.verificationValidUntil.split('.').reverse().join('-')
        );

        return validUntil < new Date();
    };

    return (
        <div className="px-3 py-2 hover:bg-slate-700/20 transition">
            <div className="flex items-center justify-between mb-1">
                <span className="text-white font-medium text-sm">
                    {device.position || device.displayName || '—'}
                </span>

                <div className="flex items-center gap-2">
                    {isVerificationExpired() && (
                        <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded text-xs">
                            Просрочена
                        </span>
                    )}

                    {canChangeHouses && (
                        <>
                            <button
                                onClick={onEdit}
                                className="p-1 bg-blue-600 hover:bg-blue-500 text-white rounded transition"
                                title="Редактировать"
                            >
                                <Edit className="w-3 h-3" />
                            </button>

                            <button
                                onClick={onDelete}
                                className="p-1 bg-red-600 hover:bg-red-500 text-white rounded transition"
                                title="Удалить"
                            >
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-slate-400">
                {device.deviceType && (
                    <span>
                        Тип:{' '}
                        <span className="text-slate-300">
                            {device.deviceType}
                        </span>
                    </span>
                )}
                {/* ✅ Отображение номера гос. реестра */}
                {device.registryNumber && (
                    <span>
                        Гос. реестр: <span className="text-slate-300">{device.registryNumber}</span>
                    </span>
                )}
                {device.deviceModel && (
                    <span>
                        Исполнение:{' '}
                        <span className="text-slate-300">
                            {device.deviceModel}
                        </span>
                    </span>
                )}

                <span>
                    Поверка:{' '}
                    <span className="text-slate-300">
                        {device.verificationDate || '—'}
                    </span>
                </span>

                <span>
                    Зав. номер:{' '}
                    <span className="text-slate-300">
                        {device.factoryNumber || '—'}
                    </span>
                </span>

                <span>
                    До:{' '}
                    <span className={isVerificationExpired() ? 'text-red-400' : 'text-slate-300'}>
                        {device.verificationValidUntil || '—'}
                    </span>
                </span>
            </div>

            {device.specs && (
                <div className="mt-1 text-xs text-slate-400">
                    <span>Тех. характеристики: </span>

                    <span className="text-slate-300 whitespace-pre-line">
                        {device.specs}
                    </span>
                </div>
            )}
        </div>
    );
};

export const DeviceListSection = ({
    title,
    icon: Icon,
    color,
    devices,
    canChangeHouses,
    onEdit,
    onCreate,
    onDelete
}) => {
    const colorClasses = {
        cyan: 'border-cyan-500/30 text-cyan-400',
        red: 'border-red-500/30 text-red-400',
        purple: 'border-purple-500/30 text-purple-400',
        blue: 'border-blue-500/30 text-blue-400'
    };

    const [borderColor, textColor] = (colorClasses[color] || colorClasses.cyan).split(' ');

    return (
        <div className={`border ${borderColor} rounded-lg overflow-hidden`}>
            <div className={`bg-slate-800/50 px-3 py-2 border-b ${borderColor} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${textColor}`} />

                    <h4 className="text-white font-medium text-sm">
                        {title}
                    </h4>

                    <span className="text-slate-500 text-xs">
                        ({devices.length})
                    </span>
                </div>

                {canChangeHouses && (
                    <button
                        onClick={onCreate}
                        className="p-1 bg-green-600 hover:bg-green-500 text-white rounded transition"
                        title="Добавить"
                    >
                        <Plus className="w-3 h-3" />
                    </button>
                )}
            </div>

            <div className="divide-y divide-slate-700/50">
                {devices.length === 0 ? (
                    <div className="p-3 text-center text-slate-500 text-sm">
                        Нет устройств
                    </div>
                ) : (
                    devices.map((device) => (
                        <DeviceItem
                            key={device.id}
                            device={device}
                            canChangeHouses={canChangeHouses}
                            onEdit={() => onEdit(device)}
                            onDelete={() => onDelete(device)}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export const DeviceSection = ({
    title,
    icon: Icon,
    color,
    device,
    canChangeHouses,
    onEdit,
    onCreate,
    onDelete,
    onGoToDevice
}) => {
    const colorClasses = {
        cyan: 'border-cyan-500/30 text-cyan-400',
        red: 'border-red-500/30 text-red-400',
        purple: 'border-purple-500/30 text-purple-400',
        blue: 'border-blue-500/30 text-blue-400'
    };

    const [borderColor, textColor] = (colorClasses[color] || colorClasses.cyan).split(' ');

    const isVerificationExpired = () => {
        if (!device?.verificationValidUntil) return false;

        const validUntil = new Date(
            device.verificationValidUntil.split('.').reverse().join('-')
        );

        return validUntil < new Date();
    };

    return (
        <div className={`border ${borderColor} rounded-lg overflow-hidden`}>
            <div className={`bg-slate-800/50 px-3 py-2 border-b ${borderColor} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${textColor}`} />

                    <h4 className="text-white font-medium text-sm">
                        {title}
                    </h4>
                </div>

                <div className="flex items-center gap-1">
                    {device && (
                        <>
                            {onGoToDevice && (
                                <button
                                    onClick={onGoToDevice}
                                    className="p-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded transition"
                                    title="Открыть устройство"
                                >
                                    <ExternalLink className="w-3 h-3" />
                                </button>
                            )}

                            {canChangeHouses && (
                                <>
                                    <button
                                        onClick={onEdit}
                                        className="p-1 bg-blue-600 hover:bg-blue-500 text-white rounded transition"
                                        title="Редактировать"
                                    >
                                        <Edit className="w-3 h-3" />
                                    </button>

                                    <button
                                        onClick={onDelete}
                                        className="p-1 bg-red-600 hover:bg-red-500 text-white rounded transition"
                                        title="Удалить"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </>
                            )}
                        </>
                    )}

                    {!device && canChangeHouses && (
                        <button
                            onClick={onCreate}
                            className="p-1 bg-green-600 hover:bg-green-500 text-white rounded transition"
                            title="Добавить"
                        >
                            <Plus className="w-3 h-3" />
                        </button>
                    )}
                </div>
            </div>

            {device ? (
                <div className="p-3 space-y-1 text-sm">
                    {onGoToDevice ? (
                        <button
                            onClick={onGoToDevice}
                            className="w-full flex items-center justify-between hover:bg-slate-700/30 transition rounded px-0 py-1 group text-left"
                            title="Перейти к устройству"
                        >
                            <span className="text-slate-400 text-sm">
                                Исполнение (Модель):
                            </span>

                            <div className="flex items-center gap-2">
                                <span className="text-white text-sm font-medium text-right">
                                    {device.deviceModel ||
                                        DEVICE_TYPE_MAP[device.deviceType] ||
                                        device.deviceType ||
                                        '—'}
                                </span>

                                <ExternalLink className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0 opacity-60 group-hover:opacity-100 transition" />
                            </div>
                        </button>
                    ) : (
                        <InfoLine
                            label="Исполнение (Модель)"
                            value={
                                device.deviceModel ||
                                DEVICE_TYPE_MAP[device.deviceType] ||
                                device.deviceType ||
                                '—'
                            }
                        />
                    )}

                    <InfoLine label="Описание" value={device.displayName} />
                    <InfoLine label="Заводской номер" value={device.factoryNumber} />
                    <InfoLine label="Дата выпуска" value={device.releaseDate} />
                    <InfoLine label="Дата поверки" value={device.verificationDate} />
                    <InfoLine
                        label="Поверка до"
                        value={device.verificationValidUntil}
                        highlight={isVerificationExpired()}
                    />
                    <InfoLine label="CRC16 настроек" value={device.crc16} />
                    <InfoLine label="Дата посл. изм. настроек" value={device.lastSettingsChange} />
                    <InfoLine label="Перемычка" value={device.jumperPosition} />

                    {device.algorithmFormula && (
                        <div className="flex items-start justify-between py-1">
                            <span className="text-slate-400 text-sm flex-shrink-0 mr-4">
                                Алгоритм учета:
                            </span>

                            <span className="text-white text-sm font-medium text-right whitespace-pre-line break-words">
                                {device.algorithmFormula}
                            </span>
                        </div>
                    )}

                    <InfoLine label="Параметр холодного источника" value={device.coldSourceParam} />
                    <InfoLine label="Глубина архивов" value={device.archiveDepth} />
                </div>
            ) : (
                <div className="p-3 text-center text-slate-500 text-sm">
                    Не добавлен
                </div>
            )}
        </div>
    );
};